import { Either } from "effect";
import { BadRequestError } from "../error/errors.ts";

export const MIN_INTERNAL_REFERENCE_MAJOR = 1;
export const MAX_INTERNAL_REFERENCE_MAJOR = 9;
export const MIN_INTERNAL_REFERENCE_MINOR = 1;
export const MAX_INTERNAL_REFERENCE_MINOR = 99;
export const DEFAULT_INTERNAL_REFERENCE_PREFIX = "";

export interface InternalReferencePosition {
  major: number;
  minor: number;
}

export interface AllocatedInternalReference {
  reference: string;
  nextMajor: number;
  nextMinor: number;
}

export const INTERNAL_REFERENCE_SERIES_EXHAUSTED_ERROR_CODE = "INTERNAL_REFERENCE_SERIES_EXHAUSTED";

export class InternalReferenceSeriesExhaustedError extends BadRequestError {
  constructor() {
    super("Internal project reference numbering series exhausted", {
      code: INTERNAL_REFERENCE_SERIES_EXHAUSTED_ERROR_CODE,
    });
  }
}

const INVALID_REFERENCE_MESSAGE = "Invalid internal project reference";

export function formatInternalReference({
  prefix,
  major,
  minor,
}: InternalReferencePosition & { prefix: string }): string {
  return `${prefix}.${major}.${minor.toString().padStart(2, "0")}`;
}

export function nextFreeInternalReference({
  prefix,
  nextMajor,
  nextMinor,
  takenReferences,
}: {
  prefix: string;
  nextMajor: number;
  nextMinor: number;
  takenReferences: string[];
}): Either.Either<AllocatedInternalReference, BadRequestError> {
  if (nextMajor > MAX_INTERNAL_REFERENCE_MAJOR) {
    return Either.left(new InternalReferenceSeriesExhaustedError());
  }

  if (!isInternalReferencePositionInRange(nextMajor, nextMinor)) {
    return Either.left(new BadRequestError(INVALID_REFERENCE_MESSAGE));
  }

  const taken = new Set(takenReferences.map((reference) => reference.toLowerCase()));
  let major = nextMajor;
  let minor = nextMinor;

  while (isInternalReferencePositionInRange(major, minor)) {
    const reference = formatInternalReference({ prefix, major, minor });

    if (!taken.has(reference.toLowerCase())) {
      const next = incrementInternalReferencePosition({ major, minor });
      return Either.right({ reference, nextMajor: next.major, nextMinor: next.minor });
    }

    const next = incrementInternalReferencePosition({ major, minor });
    major = next.major;
    minor = next.minor;
  }

  return Either.left(new InternalReferenceSeriesExhaustedError());
}

export function incrementInternalReferencePosition({
  major,
  minor,
}: InternalReferencePosition): InternalReferencePosition {
  if (minor < MAX_INTERNAL_REFERENCE_MINOR) {
    return { major, minor: minor + 1 };
  }
  return { major: major + 1, minor: MIN_INTERNAL_REFERENCE_MINOR };
}

export function isInternalReferencePositionInRange(major: number, minor: number): boolean {
  return (
    Number.isInteger(major) &&
    Number.isInteger(minor) &&
    major >= MIN_INTERNAL_REFERENCE_MAJOR &&
    major <= MAX_INTERNAL_REFERENCE_MAJOR &&
    minor >= MIN_INTERNAL_REFERENCE_MINOR &&
    minor <= MAX_INTERNAL_REFERENCE_MINOR
  );
}
